"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import Section from "@/components/static/Section";
import Headline from "@/components/static/Headline";
import Text from "@/components/static/Text";
import parse from "html-react-parser";
import Image from "next/image";
import Button from "@/components/static/Button";
import { SectionPadding, HeadlineProps, ImageProps, ButtonProps } from "@/lib/types";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { Content } from "@radix-ui/react-accordion";

type InnerProps = {
    headline: string;
    topline: string;
    text: string;
    image: ImageProps;
    button: ButtonProps;
};

type Content = {
    headline: HeadlineProps;
    text: string;
    settings: {
        padding: SectionPadding;
        columns: number;
    };
    inner: InnerProps[];
    button: ButtonProps;
};

const slideVariants = {
    hidden: {
        opacity: 0,
        height: 0,
        y: 50,
    },
    visible: {
        opacity: 1,
        height: "auto",
        y: 0,
    },
};

export default function Layout_423({ content }: { content: Content }) {
    const [hoveredFeatureIdx, setHoveredFeatureIdx] = useState<number | null>(null);
    return (
        <Section dataComponent='Layout_423' settings={content.settings}>
            <div className='container'>
                <div className='mx-auto mb-12 w-full max-w-lg text-center md:mb-18 lg:mb-20'>
                    <Headline
                        align='center'
                        tag={content.headline.tag}
                        topline={content.headline.topline}
                    >
                        {content.headline.title}
                    </Headline>
                    {content.text && (
                        <Text className='prose md:text-md'>{parse(content.text)}</Text>
                    )}
                </div>

                <div className='flex flex-col items-stretch justify-between gap-8 lg:flex-row'>
                    {content.inner.map((child, index: number) => (
                        <Link
                            key={index}
                            href={child.button?.link?.url ?? "#"}
                            className='overflow-hidden bg-bgElement h-auto rounded-xl lg:w-1/2'
                        >
                            <div className='h-[470px] w-full overflow-hidden'>
                                <Image
                                    src={child.image.url}
                                    alt={child.image.alt}
                                    width={child.image.width}
                                    height={child.image.height}
                                    className='size-full object-cover'
                                />
                            </div>

                            <div className='p-6 lg:p-8'>
                                <Headline
                                    tag='h3'
                                    topline={child.topline}
                                    className='text-h3'
                                    wrapperClassName='text-primary mb-0'
                                >
                                    {child.headline}
                                </Headline>
                                <p className='mt-1 text-textSecondary leading-[150%] text-md'>
                                    {child.text}
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
                {content.button.check && (
                    <div className='flex items-center justify-center mt-10 lg:mt-20'>
                        <Button
                            href={content.button.link.url ?? "#"}
                            as='link'
                            type={content.button.type}
                            className=''
                        >
                            {content.button.link.title}
                        </Button>
                    </div>
                )}
            </div>
        </Section>
    );
}
